import type * as api from '@gitbook/api-client';
import type { Block, Document, Inline } from '@gitbook/slate';

import { getTableRecordValue } from './table';
import {
    BLOCK_CONTENT_REF,
    BLOCK_DRAWING,
    BLOCK_FILE,
    BLOCK_IMAGE,
    BLOCK_OPENAPI,
    BLOCK_OPENAPI_OPERATION,
    BLOCK_OPENAPI_SCHEMAS,
    BLOCK_OPENAPI_WEBHOOK,
    BLOCK_REUSABLE_CONTENT,
    BLOCK_TABLE,
    type BlockContentRefData,
    type BlockDrawingData,
    type BlockFileData,
    type BlockImageData,
    type BlockOpenAPIData,
    type BlockOpenAPIOperationData,
    type BlockOpenAPISchemasData,
    type BlockReusableContentData,
    type BlockTableData,
    INLINE_BUTTON,
    INLINE_IMAGE,
    INLINE_LINK,
    INLINE_MENTION,
    type InlineButtonData,
    type InlineImageData,
    type InlineLinkData,
    type InlineMentionData,
} from './types';

/**
 * A map of content references by node key.
 */
type ContentReferenceMap = { [nodeKey: string]: api.ContentRef[] };

/**
 * Extract content references from a node.
 */
export function extractContentRefs(document: Document): ContentReferenceMap {
    const result: ContentReferenceMap = {};

    mapContentRefs(document, (node, ref) => {
        result[node.key] = (result[node.key] ?? []).concat(ref);
        return ref;
    });

    return result;
}

/**
 * Map all content-refs in a document to new value.
 */
export function mapContentRefs(
    document: Document,
    mapContentRef: (node: Block | Inline, ref: api.ContentRef) => api.ContentRef
): Document {
    return document.mapDescendants((node) => {
        if (node.object === 'block') {
            switch (node.type) {
                case BLOCK_OPENAPI:
                case BLOCK_OPENAPI_OPERATION:
                case BLOCK_OPENAPI_SCHEMAS:
                case BLOCK_OPENAPI_WEBHOOK:
                case BLOCK_REUSABLE_CONTENT:
                case BLOCK_CONTENT_REF:
                case BLOCK_FILE:
                case BLOCK_DRAWING: {
                    const data = node.data.toObject<
                        | BlockContentRefData
                        | BlockOpenAPIOperationData
                        | BlockOpenAPIData
                        | BlockOpenAPISchemasData
                        | BlockReusableContentData
                        | BlockDrawingData
                        | BlockFileData
                    >();

                    if (data.ref) {
                        const ref = mapContentRef(node, data.ref);
                        return ref === data.ref ? node : node.setData(['ref'], ref);
                    }

                    return node;
                }
                case BLOCK_IMAGE: {
                    const data = node.data.toObject<BlockImageData>();
                    const ref = mapContentRef(node, data.ref);
                    const refDark = data.refDark ? mapContentRef(node, data.refDark) : undefined;
                    return ref === data.ref && refDark === data.refDark
                        ? node
                        : node.setData(['ref'], ref).setData(['refDark'], refDark);
                }
                case BLOCK_TABLE: {
                    const initialData = node.data.toObject<BlockTableData>();
                    let data = initialData;

                    Object.values(data.definition).forEach((columnDefinition) => {
                        if (columnDefinition.type === 'content-ref') {
                            const columnId = columnDefinition.id;

                            Object.entries(data.records).forEach(([recordId, record]) => {
                                const ref = getTableRecordValue(record, columnDefinition);

                                if (ref) {
                                    const mappedRef = mapContentRef(node, ref);
                                    if (mappedRef !== ref) {
                                        data.records[recordId].values[columnId] = mappedRef;
                                        data = { ...data };
                                    }
                                }
                            });
                        } else if (columnDefinition.type === 'files') {
                            const columnId = columnDefinition.id;

                            Object.entries(data.records).forEach(([recordId, record]) => {
                                const fileIds = getTableRecordValue(record, columnDefinition);

                                if (Array.isArray(fileIds)) {
                                    const newFileIds: string[] = [];
                                    let hasChanged = false;

                                    fileIds.forEach((fileId) => {
                                        const mappedRef = mapContentRef(node, {
                                            kind: 'file',
                                            file: fileId,
                                        });

                                        if (mappedRef.kind !== 'file') {
                                            throw new Error(
                                                `Expected file reference, got ${mappedRef.kind} for fileId ${fileId}`
                                            );
                                        }

                                        if (mappedRef.file !== fileId) {
                                            hasChanged = true;
                                        }

                                        newFileIds.push(mappedRef.file);
                                    });

                                    if (hasChanged) {
                                        data.records[recordId].values[columnId] = newFileIds;
                                        data = { ...data };
                                    }
                                }
                            });
                        } else if (columnDefinition.type === 'users') {
                            const columnId = columnDefinition.id;

                            Object.entries(data.records).forEach(([recordId, record]) => {
                                const userIds = getTableRecordValue(record, columnDefinition);

                                if (Array.isArray(userIds)) {
                                    const newUserIds: string[] = [];
                                    let hasChanged = false;

                                    userIds.forEach((userId) => {
                                        const mappedRef = mapContentRef(node, {
                                            kind: 'user',
                                            user: userId,
                                        });

                                        if (mappedRef.kind !== 'user') {
                                            throw new Error(
                                                `Expected user reference, got ${mappedRef.kind} for userId ${userId}`
                                            );
                                        }

                                        if (mappedRef.user !== userId) {
                                            hasChanged = true;
                                        }

                                        newUserIds.push(mappedRef.user);
                                    });

                                    if (hasChanged) {
                                        data.records[recordId].values[columnId] = newUserIds;
                                        data = { ...data };
                                    }
                                }
                            });
                        } else if (columnDefinition.type === 'image') {
                            const columnId = columnDefinition.id;

                            Object.entries(data.records).forEach(([recordId, record]) => {
                                const value = getTableRecordValue(record, columnDefinition);

                                if (!value) {
                                    return;
                                }

                                const ref = 'ref' in value ? value.ref : value;
                                if (!ref) {
                                    return;
                                }

                                const mappedRef = mapContentRef(node, ref);
                                if (mappedRef !== ref) {
                                    if (mappedRef.kind !== 'file' && mappedRef.kind !== 'url') {
                                        throw new Error(
                                            `Expected file or url reference, got ${mappedRef.kind} for image column`
                                        );
                                    }

                                    const mappedValue: api.DocumentTableImageRecord = {
                                        objectFit: 'ref' in value ? value.objectFit : undefined,
                                        ref: mappedRef,
                                    };

                                    data.records[recordId].values[columnId] = mappedValue;
                                    data = { ...data };
                                }
                            });
                        }
                    });

                    return data !== initialData ? node.setData(['records'], data.records) : node;
                }
            }
        }

        if (node.object === 'inline') {
            switch (node.type) {
                case INLINE_LINK:
                case INLINE_MENTION:
                case INLINE_IMAGE:
                case INLINE_BUTTON: {
                    const data = node.data.toObject<
                        InlineImageData | InlineMentionData | InlineLinkData | InlineButtonData
                    >();
                    const ref = mapContentRef(node, data.ref);
                    return ref === data.ref ? node : node.setData(['ref'], ref);
                }
            }
        }

        return node;
    });
}

/**
 * Map all content-refs in a document to absolute values using a given space.
 */
export function mapContentRefsToAbsolute(
    document: Document,
    { spaceId, pageId }: { spaceId: string; pageId: string }
) {
    return mapContentRefs(document, (_node, ref) => {
        if (ref.kind === 'page') {
            return {
                ...ref,
                space: ref.space ?? spaceId,
            };
        }
        if (ref.kind === 'anchor') {
            return {
                ...ref,
                space: ref.space ?? spaceId,
                page: ref.page ?? pageId,
            };
        }

        return ref;
    });
}
